import {format} from 'date-fns';
import {PagewebEnv} from '../PagewebEnv.interface';
import {ServerEnv} from '../ServerEnv.enum';
import {LogLevelType} from '../../../log/logger';

export function createPagewebEnv(rawEnv: {[key: string]: any}): PagewebEnv {
  const isStorybook = !!rawEnv.STORYBOOK;
  const isLocal = rawEnv.EXEC_ENV === 'local';
  const isProduction = rawEnv.NODE_ENV === 'production';
  const useHttps = rawEnv.USE_HTTPS === 'YES'
  const customLocalHost = rawEnv.CUSTOM_WEB_LOCALHOST;
  const customBffLocalhost = rawEnv.CUSTOM_BFF_LOCALHOST;
  return {
    envName: rawEnv.ENV_NAME || 'dev',
    nodeEnv: rawEnv.NODE_ENV,
    version: rawEnv.VERSION,
    isLocal,
    webOrigin: isStorybook
      ? ''
      : isLocal
        ? `${useHttps ? 'https' : 'http'}://${customLocalHost || 'localhost.kakao.com'}`
        : rawEnv.WEB_ORIGIN,
    bffOrigin: isLocal ? `${useHttps ? 'https' : 'http'}://${customBffLocalhost || 'bff-localhost.kakao.com'}` : rawEnv.BFF_ORIGIN,
    assetPrefix: rawEnv.ASSET_PREFIX
      ? `${process.env.ASSET_PREFIX}/${rawEnv.VERSION}`
      : '',
    linkHostname: isLocal
      ? 'link-localhost.kakao.com'
      : rawEnv.LINK_HOSTNAME || 'link-page.kakao.com',
    linkOrigin: `https://${rawEnv.LINK_HOSTNAME || 'link-page.kakao.com'}${
      isLocal ? `:${rawEnv.PORT || 3000}` : ''
    }`,
    gatewayOrigin: rawEnv.GATEWAY_ORIGIN,
    billingApiOrigin: rawEnv.BILLING_API_ORIGIN,
    tckApiOrigin: rawEnv.TCK_API_ORIGIN,
    kakaoBankApiOrigin: rawEnv.KAKAO_BANK_API_ORIGIN,
    isProduction,
    isProductionService:
      rawEnv.ENV_NAME === ServerEnv.Real || rawEnv.ENV_NAME === ServerEnv.Beta,
    useHttps: useHttps,
    port:
      Number(rawEnv.PORT) || Number(rawEnv.WEB_PORT) || (isLocal ? (useHttps ? 443 : 80) : 3000),
    kageOrigin: rawEnv.KAGE_ORIGIN || 'https://dn-img-page.kakao.com',
    kageUrlPrefix:
      rawEnv.KAGE_URL_PREFIX ||
      '//dn-img-page.kakao.com/download/resource?kid=',
    stonKageOrigin:
      rawEnv.STON_KAGE_ORIGIN || 'https://page-images.kakaoentcdn.com',
    stonKageUrlPrefix:
      rawEnv.STON_KAGE_URL_PREFIX ||
      '//page-images.kakaoentcdn.com/download/resource?kid=',
    dreamSecurityV2SdkUrl: rawEnv.DREAM_SECURITY_V2_SDK_URL,
    useSandboxKakao: rawEnv.USE_REAL_KAKAO_SDK !== 'YES',
    kakaoAccountLogoutUrl:
      rawEnv.KAKAO_ACCOUNT_LOGOUT_URL || 'https://page.kakao.com/logout',
    kakaoSdkFileName: rawEnv.KAKAO_SDK_FILE_NAME || 'kakao.min',
    kakaoEmoticonSdkJs:
      rawEnv.KAKAO_EMOTICON_SDK_JS ||
      'https://mk.kakaocdn.net/dn/emoticon/static/javascripts/sdk/1.4.0/kakao-1.4.0-k.min.js',
    kakaoEmoticonSdkCssMobile:
      rawEnv.KAKAO_EMOTICON_SDK_CSS_MOBILE ||
      'https://mk.kakaocdn.net/dn/emoticon/static/stylesheets/sdk/1.1.3/emoticon_m-1.1.3.css',
    kakaoEmoticonSdkCssPc:
      rawEnv.KAKAO_EMOTICON_SDK_CSS_PC ||
      'https://mk.kakaocdn.net/dn/emoticon/static/stylesheets/sdk/1.1.3/emoticon-1.1.3.css',
    kakaoSdkId: rawEnv.KAKAO_SDK_ID || '49bbb48c5fdb0199e5da1b89de359484',
    kakaoApiOrigin: rawEnv.KAKAO_API_ORIGIN || 'kapi.kakao.com',
    kakaoAuthApiOrigin: rawEnv.KAKAO_AUTH_API_ORIGIN || 'kauth.kakao.com',
    daSdkUrl: rawEnv.DA_SDK_URL || '//t1.kakaocdn.net/kas/static/ba.min.js',
    daNaSdkUrl: rawEnv.DA_NA_SDK_URL || '//t1.kakaocdn.net/kas/static/na.min.js',
    uptime: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    drmServerHost: rawEnv.LICENSE_SERVER_HOST,
    iniWidevineLicenseServerUrl: rawEnv.INI_WIDEVINE_LICENSE_URL,
    iniFairplayLicenseServerUrl: rawEnv.INI_FAIRPLAY_LICENSE_URL,
    billingWebOrigin:
      rawEnv.BILLING_WEB_ORIGIN || 'https://page-billing-web.kakao.com',
    stageUrl: rawEnv.STAGE_URL || 'https://pagestage.kakao.com/',
    adminSiteHost: rawEnv.ADMIN_SITE_HOST || 'https://charlie.kakaopage.com',
    cmsApiOrigin: rawEnv.CMS_API_ORIGIN || 'https://api-cms.kakaopage.com',
    logLevelServer: isLocal
      ? LogLevelType.Err
      : rawEnv.LOG_LEVEL_SERVER || LogLevelType.Err,
    logLevelBrowser: isLocal
      ? LogLevelType.Err
      : rawEnv.LOG_LEVEL_BROWSER || LogLevelType.Err,
    showConsoleLogServer: rawEnv.SHOW_CONSOLE_LOG_SERVER === 'YES',
    showConsoleLogBrowser: rawEnv.SHOW_CONSOLE_LOG_BROWSER === 'YES',
    sendBrowserLogToServer: rawEnv.SEND_BROWSER_LOG_TO_SERVER === 'YES',
    appSchemeQrName: isLocal
      ? 'AppSchemeQrLocal'
      : rawEnv.APP_SCHEME_QR_NAME || 'AppSchemeQrReal',
    sentryDsn:
      isLocal
        ? 'https://e66b05fa9afb493ba71b74b7f290d09d@aem-kakao-collector.onkakao.net/24731'
        : rawEnv.SENTRY_DSN ||
          'https://2a02617eacd14356b19e5e8a5e4a2e66@aem-kakao-collector.onkakao.net/15193',
    isSentryOn:
      rawEnv.IS_SENTRY_ON === undefined || rawEnv.IS_SENTRY_ON === ''
        ? isProduction
        : rawEnv.IS_SENTRY_ON === 'YES',
    kemOrigin: rawEnv.KEM_ORIGIN || 'https://kakaoent.com',
    get policyPrivacyUrl() {
      return `${this.kemOrigin}/privacy/policy?service=kakaopage`;
    },
    get policyTargetAdUrl() {
      return `${this.kemOrigin}/privacy/targetad?service=kakaopage`;
    },
    kakaoCsUrl: // TODO: TCK 측 마이그레이션 이관 완료 까진 카카오 고객센터 링크 사용. feature flag 로 제어
      rawEnv.KAKAO_CS_URL ||
      'https://cs.kakao.com/requests?locale=ko&service=10',
    // TODO: TCK 측 마이그레이션 이관 되면 제거. 웹뷰용 카카오 고객센터임. feature flag 로 제어
    kakaoCsCenterOrigin: rawEnv.KAKAO_CS_CENTER_ORIGIN || 'https://cs-center.kakao.com',
  };
}
